/**
 * W-13 Conversion Engine — local, privacy-safe funnel tracker for the
 * World Cup activation sequence. Reuses `vs:event` bus from `trackEvent`
 * (no extra network calls). Never invents users or metrics — counts only
 * real events emitted by production code.
 *
 * Steps:
 *  visit → register → nation → first_prediction → first_reward
 *        → first_share → first_referral
 *
 * Multi-country aware via `countryExperience.ts` (BR / MX / AR / IN).
 */
import type { EventType } from "@/types";

export type FunnelStep =
  | "visit"
  | "register"
  | "nation"
  | "first_prediction"
  | "first_reward"
  | "first_share"
  | "first_referral";

export const FUNNEL_STEPS: FunnelStep[] = [
  "visit",
  "register",
  "nation",
  "first_prediction",
  "first_reward",
  "first_share",
  "first_referral",
];

export const FUNNEL_LABELS: Record<FunnelStep, string> = {
  visit: "Visitantes",
  register: "Registros",
  nation: "Nación elegida",
  first_prediction: "Primera predicción",
  first_reward: "Primera recompensa",
  first_share: "Primer share",
  first_referral: "Primer referral",
};

/** Conversion-rate targets (% from previous step). */
export const FUNNEL_TARGETS: Record<FunnelStep, number> = {
  visit: 100,
  register: 35,
  nation: 80,
  first_prediction: 60,
  first_reward: 95,
  first_share: 25,
  first_referral: 10,
};

const EVENT_TO_STEP: Partial<Record<EventType, FunnelStep>> = {
  app_open: "visit",
  welcome_dashboard_viewed: "visit" as never, // tolerated if unknown
  registration_completed: "register",
  nation_selected: "nation",
  first_prediction_completed: "first_prediction",
  first_reward_seen: "first_reward",
  first_share_completed: "first_share",
  first_referral_sent: "first_referral",
} as Partial<Record<EventType, FunnelStep>>;

const KEY = "vs:conversion_funnel_v1";
const DROPOFF_KEY = "vs:conversion_dropoff_v1";

export interface FunnelSnapshot {
  counts: Record<FunnelStep, number>;
  firstAt: Partial<Record<FunnelStep, number>>;
  country: string | null;
  updatedAt: number;
}

function emptySnapshot(): FunnelSnapshot {
  const counts = {} as Record<FunnelStep, number>;
  for (const s of FUNNEL_STEPS) counts[s] = 0;
  return { counts, firstAt: {}, country: null, updatedAt: Date.now() };
}

function read(): FunnelSnapshot {
  if (typeof window === "undefined") return emptySnapshot();
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return emptySnapshot();
    const parsed = JSON.parse(raw) as FunnelSnapshot;
    // Re-hydrate any missing step keys after an upgrade.
    for (const s of FUNNEL_STEPS) if (parsed.counts[s] == null) parsed.counts[s] = 0;
    return parsed;
  } catch {
    return emptySnapshot();
  }
}

function write(snap: FunnelSnapshot) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(KEY, JSON.stringify(snap)); } catch { /* noop */ }
}

export function getFunnelSnapshot(): FunnelSnapshot { return read(); }

export function setFunnelCountry(country: string | null) {
  const s = read();
  s.country = country ? country.toUpperCase() : null;
  s.updatedAt = Date.now();
  write(s);
}

export function recordFunnelStep(step: FunnelStep) {
  const s = read();
  s.counts[step] = (s.counts[step] ?? 0) + 1;
  if (s.firstAt[step] == null) s.firstAt[step] = Date.now();
  s.updatedAt = Date.now();
  write(s);
  emit();
  checkDropoffs(s);
}

export interface FunnelRate {
  step: FunnelStep;
  rate: number;        // % from previous step (visit = 100)
  target: number;
  ok: boolean;
}

export function computeRates(snap: FunnelSnapshot = read()): FunnelRate[] {
  const out: FunnelRate[] = [];
  let prev = snap.counts.visit || 0;
  for (const step of FUNNEL_STEPS) {
    const c = snap.counts[step] || 0;
    let rate = 0;
    if (step === "visit") rate = c > 0 ? 100 : 0;
    else rate = prev > 0 ? (c / prev) * 100 : 0;
    const target = FUNNEL_TARGETS[step];
    out.push({ step, rate, target, ok: rate >= target });
    prev = c;
  }
  return out;
}

/** Returns the first step that under-performs its target (sample ≥ 10). */
export function detectBottleneck(snap: FunnelSnapshot = read()): FunnelRate | null {
  const rates = computeRates(snap);
  // Need a minimum sample of visits to be meaningful.
  if ((snap.counts.visit ?? 0) < 10) return null;
  for (const r of rates) {
    if (r.step === "visit") continue;
    if (!r.ok) return r;
  }
  return null;
}

function checkDropoffs(snap: FunnelSnapshot) {
  if (typeof window === "undefined") return;
  const b = detectBottleneck(snap);
  if (!b) return;
  let last: string | null = null;
  try { last = window.localStorage.getItem(DROPOFF_KEY); } catch { /* noop */ }
  if (last === b.step) return; // de-dupe consecutive identical detections
  try { window.localStorage.setItem(DROPOFF_KEY, b.step); } catch { /* noop */ }
  // Fire global event so dashboards / War Room can react. Audit pipeline
  // is intentionally optional here to avoid coupling to network state.
  try {
    window.dispatchEvent(new CustomEvent("vs:conversion-dropoff", { detail: b }));
  } catch { /* noop */ }
  void import("@/lib/audit").then(({ trackEvent }) => {
    void trackEvent("conversion_dropoff_detected", {
      step: b.step, rate: Math.round(b.rate), target: b.target,
      country: snap.country,
    });
  });
}

const CHANGE = "vs:conversion-funnel-changed";
function emit() {
  if (typeof window === "undefined") return;
  try { window.dispatchEvent(new CustomEvent(CHANGE)); } catch { /* noop */ }
}
export function subscribeFunnel(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(CHANGE, cb);
  return () => window.removeEventListener(CHANGE, cb);
}

let booted = false;
export function bootstrapConversionFunnel(): void {
  if (typeof window === "undefined" || booted) return;
  booted = true;
  // Count this session as a visit once.
  const sessionKey = "vs:conversion_visit_session";
  try {
    if (!window.sessionStorage.getItem(sessionKey)) {
      window.sessionStorage.setItem(sessionKey, "1");
      recordFunnelStep("visit");
    }
  } catch { recordFunnelStep("visit"); }

  window.addEventListener("vs:event", (e: Event) => {
    const detail = (e as CustomEvent<{ event_type: string }>).detail;
    if (!detail) return;
    const step = EVENT_TO_STEP[detail.event_type as EventType];
    if (step) recordFunnelStep(step);
  });
}